import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { usePortfolioStore } from "../stores/portfolioStore";
import ContactInfo from "./ContactInfo";
import IntroVideoModal from "./IntroVideoModal";
import IntroVideoCard from "./IntroVideoCard";
import LaserBorder from "./layout/LaserBorder";
import MagneticButton from "./motion/MagneticButton";
import {
  canUseCinematicMotion,
  gsap,
  scrollToElement,
} from "../lib/motion";

const heroStack = [
  { key: "frontend", items: ["React", "Next.js", "TypeScript", "React Native", "Angular", "Vue.js", "Elm"] },
  { key: "backend", items: ["Go", "Node.js", "Python", "FastAPI", "C#", "F#", "PHP", "Convex"] },
  { key: "data", items: ["PostgreSQL", "MySQL", "SQLite", "MSSQL", "Neo4j", "MongoDB"] },
  { key: "cloudDevOps", items: ["AWS", "Azure", "Terraform", "Docker", "GitHub Actions"] },
];

interface BannerComponentProps {
  scrollToBody: () => void;
}

const BannerComponent: React.FC<BannerComponentProps> = ({ scrollToBody }) => {
  const { startTypingAnimation } = usePortfolioStore();
  const navigate = useNavigate();
  const introRef = useRef<HTMLDivElement>(null);

  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [introOpen, setIntroOpen] = useState(false);
  const closeIntro = useCallback(() => setIntroOpen(false), []);

  useEffect(() => {
    startTypingAnimation();
  }, [startTypingAnimation]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // GSAP intro timeline
  useEffect(() => {
    if (!introRef.current || !canUseCinematicMotion()) return;

    const ctx = gsap.context(() => {
      gsap.from("[data-hero-intro]", {
        y: 28,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power2.out",
        delay: 0.15,
      });
    }, introRef);

    return () => ctx.revert();
  }, []);

  const goToWork = () => {
    navigate({ to: "/projects" });
  };

  const goToConsult = () => {
    navigate({ to: "/packages", hash: "consult" });
  };

  return (
    <div
      className="hero-section relative lg:min-h-screen md:!pt-24 md:!pb-24 lg:!pt-28 lg:!pb-20 w-full overflow-hidden transition-all duration-300 ease-in-out"
      style={
        {
          transform: `translateY(${Math.max(-50, -scrollY * 0.5)}px)`,
          willChange: "transform",
          transformStyle: "preserve-3d",
          backfaceVisibility: "hidden",
        } as React.CSSProperties
      }
    >
      {/* Floating Particles - decorative */}
      <div className="floating-particles absolute inset-0 pointer-events-none" aria-hidden="true">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle absolute w-2 h-2 bg-cyan-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
              transform: `translate(${mousePosition.x * 20}px, ${
                mousePosition.y * 20
              }px)`,
            }}
          />
        ))}
      </div>

      {/* Floating Code Elements - decorative */}
      <div className="floating-code absolute inset-0 pointer-events-none" aria-hidden="true">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="code-element absolute text-white/10 font-mono text-sm"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${4 + Math.random() * 3}s`,
              transform: `translate(${mousePosition.x * 10}px, ${
                mousePosition.y * 10
              }px)`,
            }}
          >
            {["{", "}", "(", ")", "=>", "const", "let", "function"][i]}
          </div>
        ))}
      </div>

      {/* Animated Background Grid - decorative */}
      <div
        className="background-grid absolute inset-0 opacity-10"
        aria-hidden="true"
        style={{
          backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-radial from-purple-500/30 via-transparent to-transparent opacity-50"></div>
      <div
        className="absolute inset-0 bg-gradient-radial from-pink-500/30 via-transparent to-transparent opacity-50"
        style={{ backgroundPosition: "80% 20%" }}
      ></div>
      <div
        className="absolute inset-0 bg-gradient-radial from-cyan-500/20 via-transparent to-transparent opacity-50"
        style={{ backgroundPosition: "40% 40%" }}
      ></div>

      <div
        ref={introRef}
        className="hero-content relative h-full w-full flex items-center justify-center z-10 px-6 md:px-10"
      >
        <div className="hero-text flex-1 w-full text-left max-w-2xl lg:max-w-4xl mx-auto lg:mx-0">
          <div
            data-hero-intro
            className="hero-video w-full max-w-md ml-auto mr-auto lg:ml-0 lg:mr-0 mb-8 mt-20 md:mt-0"
            style={{
              transform: `translate(${mousePosition.x * 3}px, ${
                mousePosition.y * 3
              }px)`,
            }}
          >
            <IntroVideoCard onPlay={() => setIntroOpen(true)} />
          </div>

          <h1
            data-hero-intro
            className="hero-title text-center lg:!text-left lg:items-start items-center flex flex-col gap-2"
            style={{
              transform: `translate(${mousePosition.x * 3}px, ${
                mousePosition.y * 3
              }px)`,
            }}
          >
            <span className="hero-greeting">Hello, I&apos;m</span>
            <span className="hero-name">Don Benary</span>
            <p className="text-xl md:text-2xl text-white/95 font-medium flex md:flex-row flex-col md:flex-wrap md:justify-center lg:justify-start xl:flex-nowrap gap-2 items-center">
              <span className="text-cyan-400 font-semibold whitespace-nowrap shrink-0">
                Full-Stack Developer
              </span>
              <span className="whitespace-nowrap shrink-0">& Founder of</span>
              <a
                href="https://evently.ph"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 font-bold px-2 py-1 pb-2 rounded-md bg-orange-500/15 border border-orange-500/30 shadow-lg hover:bg-orange-500/25 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 relative w-fit shrink-0"
              >
                evently.ph
              </a>
            </p>
          </h1>

          <div
            data-hero-intro
            className="mb-6"
            style={{
              transform: `translateY(${Math.min(16, Math.max(0, scrollY * 0.1))}px)`,
              opacity: Math.max(0.7, 1 - scrollY * 0.001),
            }}
          >
            <p className="hero-description">
              Senior Full-Stack Engineer and Tech Lead with 12+ years
              designing, building, and deploying scalable web and mobile
              products. I&apos;ve led engineering teams and built applications from first commit to production.
            </p>
          </div>

          {/* Client CTAs */}
          <div
            data-hero-intro
            className="flex flex-col sm:flex-row gap-3 mb-8 justify-center lg:justify-start"
          >
            <MagneticButton
              className="px-6 py-3 rounded-lg bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/20"
              onClick={goToWork}
            >
              View work
            </MagneticButton>
            <MagneticButton
              className="px-6 py-3 rounded-lg border border-white/30 text-white font-semibold hover:border-cyan-400/70 hover:text-cyan-300 transition-colors"
              onClick={goToConsult}
            >
              Book a consult
            </MagneticButton>
          </div>

          {/* Proof metrics — keep two */}
          <div
            data-hero-intro
            className="hero-stats !justify-center lg:!justify-start"
            style={{
              transform: `translateY(${Math.min(16, Math.max(0, scrollY * 0.05))}px)`,
            }}
          >
            <LaserBorder>
              <div
                style={{
                  transform: `translate(${mousePosition.x * 2}px, ${
                    mousePosition.y * 2
                  }px)`,
                }}
              >
                <span className="stat-number">12+</span>
                <span className="stat-label">Years Experience</span>
              </div>
            </LaserBorder>
            <LaserBorder>
              <div
                style={{
                  transform: `translate(${mousePosition.x * -2}px, ${
                    mousePosition.y * -2
                  }px)`,
                }}
              >
                <span className="stat-number">50+</span>
                <span className="stat-label">Projects Delivered</span>
              </div>
            </LaserBorder>
          </div>
        </div>

        <div
          className="hero-visual flex-col flex justify-center min-w-0 w-full max-w-2xl mx-auto lg:mx-0 lg:max-w-full !px-0 xl:!px-10"
          style={{
            transform: `translate(${mousePosition.x * -5}px, ${
              mousePosition.y * -5
            }px) rotateY(${mousePosition.x * 5}deg)`,
          }}
        >
          <div data-hero-intro>
            {/* Code Stack */}
            <LaserBorder className="w-full text-xs md:text-sm lg:text-base">
              <div className="code-animation w-full min-w-0 text-left whitespace-normal">
                <div className="code-line">
                  <span className="code-keyword">const</span> client ={" "}
                  <span className="whitespace-nowrap">
                    <span className="code-string">&apos;your product&apos;</span>;
                  </span>
                </div>
                <div className="code-line">
                  <span className="code-keyword">const</span> stack = {"{"}
                  {heroStack.map((group) => (
                    <div key={group.key} className="pl-8 -indent-4">
                      {group.key}: [
                      {group.items.map((item, i) => {
                        const isLast = i === group.items.length - 1;
                        return (
                          <React.Fragment key={item}>
                            <span className="whitespace-nowrap">
                              <span className="code-string">&apos;{item}&apos;</span>
                              {isLast ? "]," : ","}
                            </span>
                            {!isLast && " "}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  ))}
                  {"}"};
                </div>
                <div className="code-line">
                  <span className="code-keyword">const</span> outcome ={" "}
                  <span className="whitespace-nowrap">
                    <span className="code-string">
                      &apos;shipped & converting&apos;
                    </span>
                    ;
                  </span>
                </div>
                <div className="code-line">
                  <span className="code-keyword">return</span>{" "}
                  <span className="whitespace-nowrap">
                    <span className="code-string">&apos;Book a consult&apos;</span>
                    ;
                  </span>
                </div>
              </div>
            </LaserBorder>
          </div>
          <div
            data-hero-intro
            className="mt-6"
            style={{
              transform: `translateY(${Math.min(16, Math.max(0, scrollY * 0.06))}px)`,
            }}
          >
            <ContactInfo variant="dark" />
          </div>
        </div>
      </div>

      <div className="lg:block hidden">
        <div
          className="hero-scroll-indicator hidden lg:absolute lg:bottom-8 lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:z-20 lg:flex lg:flex-col lg:items-center"
          style={{
            transform: `translateY(${Math.max(0, scrollY * 0.02)}px)`,
            opacity: Math.max(0.3, 1 - scrollY * 0.002),
          }}
        >
          <span className="scroll-text">Scroll to explore</span>
          <button
            className="scroll-arrow text-white/80 hover:text-white transition-colors duration-300 animate-bounce hover:scale-110"
            onClick={() => {
              scrollToBody();
              scrollToElement("#process");
            }}
            aria-label="Scroll to process"
          >
            <div className="w-6 h-6 border-2 border-white/60 border-t-transparent rounded-full animate-spin"></div>
          </button>
        </div>
      </div>

      <IntroVideoModal isOpen={introOpen} onClose={closeIntro} />
    </div>
  );
};

export default BannerComponent;
